import { InnovationsHomeFilters } from '../InnovationsHomeFilters/InnovationsHomeFilters.component';
import React, { ComponentPropsWithRef, FC, useContext, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { ADVERT_FORMAT_ID, ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { useAdvert } from '@adverts/business/useAdvert';
import { GenericAd } from '@adverts/components/GenericAd.component';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { SecondaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { ContentCardLoader } from '@components/content/ContentCard/ContentCardLoader.component';
import { PlcImg } from '@components/PlcImg/PlcImg.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageHeader } from '@layout/components/PlcPageHeader/PlcPageHeader.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { buildHighlightStartupCard, buildStartupCard } from '@utils/card';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';
import { InnovationsHomeContext } from 'pages/les-innovations';

import './InnovationsHomeLayout.scss';

type Props = ComponentPropsWithRef<'main'> & {};

export const InnovationsHomeLayout: FC<Props> = ({ className, ...rest }) => {
  const rootClass = classnames('services-home-layout plc-page', className);
  const {
    startups: { data: startups, isValidating: startupsLoading },
    pagination: { data: pagination },
    fetchNextStartups,
  } = useContext(InnovationsHomeContext);
  const {
    domainsList: { data: domainsList },
    stakesList: { data: stakesList },
    sectorsList: { data: sectorsList },
  } = useContext(InnovationsHomeContext);
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);
  const adClassName = useClassNames({ all: 'plc-mt-m', m: ['plc-mt-0', 'plc-ml-m'] });

  const BREADCRUMB_STEPS: BreadCrumbStep[] = [
    {
      label: 'common.newspaper-short',
      linkProps: { as: urlEnum.journal, href: urlEnum.journal },
    },
    {
      label: 'common.innovations.catalogue-menu-title',
      linkProps: { as: urlEnum.innovations, href: urlEnum.innovations },
    },
  ];
  useAdvert({
    pageId: ADVERT_PAGE_ID.InnovationHome,
  });

  const highlightedStartup = useMemo(() => startups?.filter(item => item.selectedByCa)[0], [startups]);

  const startupsList = useMemo(() => {
    const otherStartups = startups?.filter(startup => !highlightedStartup || startup.slug !== highlightedStartup.slug);
    return (otherStartups || []).map(item =>
      buildStartupCard(item, domainsList ?? [], stakesList ?? [], sectorsList ?? [])
    );
  }, [startups, domainsList, stakesList, sectorsList]);

  function onSeeMoreClick() {
    if (fetchNextStartups) {
      fetchNextStartups();
    }
  }

  const tagProps: PlcTagProps = useMemo(
    () => ({
      isDataLoading: startupsLoading,
      level2: ATLevel2.Innovation,
      tagPageNameI18nArray: ['innovations.tag.home'],
    }),
    [startupsLoading]
  );

  return (
    <PlcPageLayout
      className={rootClass}
      metaDescI18n="innovations.home.meta-desc"
      metaTitleI18n="innovations.home.meta-title"
      tagProps={tagProps}
      {...rest}
    >
      <section className="wrapper-s">
        <div className="grid-parent-inno">
          <PlcPageHeader
            breadcrumbSteps={BREADCRUMB_STEPS}
            className="plc-mb-xl no-padding grid-header"
            i18nTitle="innovations.home.title"
            withBlockAd={false}
          >
            <div className="innovation-logo-caption">
              <Text i18nKey="innovations.home.logo-caption" variant="p" />
              <PlcImg alt="" className="" height="150px" plcSrc={{ src: 'village-by-ca-black', type: 'local' }} />
            </div>

            <Text variant="p">
              <Trans i18nKey="innovations.home.description" ns="innovations" />
            </Text>
          </PlcPageHeader>
          <div className={isSmartphone ? 'mobile-pub-center plc-mb-basis' : 'grid-pub plc-pt-basis'}>
            <GenericAd className={adClassName} formatId={ADVERT_FORMAT_ID.WideAngle} />
          </div>
        </div>
      </section>
      <section className="innovations-filter plc-py-m plc-py-up-s-l">
        <div className="wrapper-s">
          <Text className="plc-mb-basis" i18nKey="innovations.home.recherche-title" tag="h2" variant="h2" />
          <InnovationsHomeFilters />
        </div>
      </section>
      <section className="wrapper-s plc-py-m plc-py-up-s-l">
        {highlightedStartup &&
          (isSmartphone
            ? buildStartupCard(highlightedStartup, domainsList ?? [], stakesList ?? [], sectorsList ?? [], true)
            : buildHighlightStartupCard(highlightedStartup, domainsList ?? [], stakesList ?? [], sectorsList ?? []))}
        <div className={`innovation-cards grid-m-3-s-1 ${pagination ? 'plc-mb-xl' : ''}`}>
          {startupsList}
          {startupsLoading && (
            <>
              <ContentCardLoader uniqueKey="innovationsHomeLayout1" />
              <ContentCardLoader uniqueKey="innovationsHomeLayout2" />
              <ContentCardLoader uniqueKey="innovationsHomeLayout3" />
            </>
          )}
        </div>
        {pagination && !startupsLoading && (
          <div className="services-home-layout-pagination">
            <Text
              className="plc-mb-m"
              i18nKey="search.results.pagination"
              i18nOptions={{ current: pagination.current, total: pagination.count }}
              variant="p"
            />
            {pagination.hasNext && (
              <SecondaryButton onClick={onSeeMoreClick} textProps={{ i18nKey: 'search.results.see-more' }} />
            )}
          </div>
        )}
        <Text
          className="h2-up-s plc-pt-xl plc-mb-basis"
          i18nKey="innovations.home.description.titre.footer"
          tag="h2"
          variant="h3"
        />
        <Text variant="p">
          <Trans i18nKey="innovations.home.description.footer" ns="innovations" />
        </Text>
      </section>
    </PlcPageLayout>
  );
};
